/**
 * REPLAY navigation engine.
 *
 * One model for four inputs: mouse, touch, keyboard/remote and gamepad.
 * - Tracks the active input modality (html[data-input]) so focus rings and
 *   controller hints only appear when they help.
 * - Translates keyboard and gamepad input into semantic actions.
 * - Moves focus spatially (by screen geometry), with rows that remember their
 *   last focused item and scopes (dialogs, menus) that trap navigation.
 * - Yields completely while a game is running (`suspend`), and after resuming
 *   ignores the gamepad until every button is released, so the press that left
 *   the game can't trigger anything in the UI.
 */

export type NavAction = 'up' | 'down' | 'left' | 'right' | 'accept' | 'back' | 'menu' | 'prevTab' | 'nextTab' | 'prevSub' | 'nextSub' | 'secondary' | 'tertiary';
export type InputKind = 'mouse' | 'touch' | 'keyboard' | 'gamepad';
type Dir = 'up' | 'down' | 'left' | 'right';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

// ---------------------------------------------------------------------------
// Input modality
// ---------------------------------------------------------------------------
let input: InputKind = 'mouse';
const modalityListeners = new Set<(k: InputKind) => void>();

export function getInput() {
  return input;
}
export function onInputChange(fn: (k: InputKind) => void) {
  modalityListeners.add(fn);
  return () => modalityListeners.delete(fn);
}
function setInput(k: InputKind) {
  if (k === input) return;
  input = k;
  document.documentElement.dataset.input = k;
  for (const fn of modalityListeners) fn(k);
}

// ---------------------------------------------------------------------------
// Action dispatch
// ---------------------------------------------------------------------------
type Handler = (a: NavAction, source: InputKind) => boolean | void;
const handlers: Handler[] = [];

/** Register a handler (last registered runs first). Return true to consume the action. */
export function onNavAction(fn: Handler) {
  handlers.push(fn);
  return () => {
    const i = handlers.lastIndexOf(fn);
    if (i >= 0) handlers.splice(i, 1);
  };
}

let suspended = false;
let rearm = false;
/** Hand all input to someone else (the emulator). */
export function suspendNav(v: boolean) {
  suspended = v;
  if (!v) rearm = true;
}
export function isNavSuspended() {
  return suspended;
}

/** Feedback hook (sounds, haptics): told what each action ended up doing. */
export interface NavFeedback {
  action: NavAction;
  source: InputKind;
  /** Focus actually moved to another element. */
  moved: boolean;
  /** Who handled it: the focused element, a screen handler, or the engine default. */
  by: 'element' | 'handler' | 'default';
  /** Held direction auto-repeat (quieter feedback). */
  repeat: boolean;
}
let feedback: ((f: NavFeedback) => void) | null = null;
export function setNavFeedback(fn: ((f: NavFeedback) => void) | null) {
  feedback = fn;
}

export function dispatchNav(action: NavAction, source: InputKind, repeat = false) {
  if (suspended) return;
  const before = document.activeElement;
  const report = (by: NavFeedback['by']) => feedback?.({ action, source, by, repeat, moved: document.activeElement !== before });
  // 1) The focused element (and its ancestors) may intercept, e.g. a cover handling "secondary".
  const target = (document.activeElement as HTMLElement | null) ?? document.body;
  const ev = new CustomEvent('replay:nav', { bubbles: true, cancelable: true, detail: { action, source } });
  if (!target.dispatchEvent(ev)) return report('element');
  // 2) Registered handlers, most recent first.
  for (let i = handlers.length - 1; i >= 0; i--) if (handlers[i]!(action, source)) return report('handler');
  // 3) Defaults.
  switch (action) {
    case 'up':
    case 'down':
    case 'left':
    case 'right':
      moveFocus(action);
      break;
    case 'accept': {
      const el = document.activeElement as HTMLElement | null;
      if (!el || el === document.body) {
        focusFirst();
        break;
      }
      if (el instanceof HTMLInputElement && ['checkbox', 'radio'].includes(el.type)) el.click();
      else if (isTextField(el)) el.focus();
      else el.click();
      break;
    }
    case 'back':
      goBack();
      break;
    case 'prevTab':
    case 'nextTab':
      window.dispatchEvent(new CustomEvent('replay:tabs', { detail: action === 'prevTab' ? -1 : 1 }));
      break;
  }
  report('default');
}

/** Back: close the topmost layer if any, else go back in history. */
export function goBack() {
  const scope = topScope();
  if (scope instanceof HTMLElement) {
    const close = scope.querySelector<HTMLElement>('[data-nav-close]');
    if (close) return close.click();
  }
  window.dispatchEvent(new CustomEvent('replay:back'));
}

// ---------------------------------------------------------------------------
// Spatial focus
// ---------------------------------------------------------------------------
const rowMemory = new WeakMap<Element, HTMLElement>();

function topScope(): ParentNode {
  const scopes = document.querySelectorAll('[data-nav-scope]');
  return scopes.length ? scopes[scopes.length - 1]! : document;
}

function isVisible(el: HTMLElement) {
  if (el.closest('[inert],[aria-hidden="true"]')) return false;
  const r = el.getBoundingClientRect();
  if (r.width < 2 || r.height < 2) return false;
  const s = getComputedStyle(el);
  return s.visibility !== 'hidden' && s.display !== 'none' && s.pointerEvents !== 'none';
}

export function focusables(root: ParentNode = topScope()) {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => !el.hasAttribute('data-nav-skip') && isVisible(el));
}

export const isTextField = (el: Element | null): el is HTMLInputElement | HTMLTextAreaElement =>
  !!el &&
  (el instanceof HTMLTextAreaElement ||
    (el instanceof HTMLInputElement && !['checkbox', 'radio', 'button', 'submit', 'range', 'file', 'color'].includes(el.type)));

export function focusEl(el: HTMLElement, opts: { center?: boolean } = {}) {
  el.focus({ preventScroll: true });
  const smooth = !matchMedia('(prefers-reduced-motion: reduce)').matches;
  const behavior: ScrollBehavior = smooth ? 'smooth' : 'auto';
  // Horizontal rails scroll themselves; the page keeps the row comfortably in view.
  const rail = el.closest<HTMLElement>('[data-nav-row]');
  if (rail && rail.scrollWidth > rail.clientWidth) {
    const r = el.getBoundingClientRect();
    const rr = rail.getBoundingClientRect();
    const pad = Math.min(96, rr.width * 0.12);
    if (r.left < rr.left + pad) rail.scrollBy({ left: r.left - rr.left - pad, behavior });
    else if (r.right > rr.right - pad) rail.scrollBy({ left: r.right - rr.right + pad, behavior });
  }
  el.scrollIntoView({ block: opts.center ? 'center' : 'nearest', inline: 'nearest', behavior });
}

/** Focus the page's preferred entry point. */
export function focusFirst() {
  const scope = topScope();
  const preferred = (scope as ParentNode).querySelector<HTMLElement>('[data-autofocus]');
  const all = focusables(scope);
  // Prefer content over chrome, and never start on a text field (it would pop the TV keyboard).
  const target =
    preferred && isVisible(preferred)
      ? preferred
      : (all.find((e) => e.closest('main') && !isTextField(e)) ?? all.find((e) => !isTextField(e)) ?? all[0]);
  if (target) focusEl(target, { center: false });
}

function rangeGap(a1: number, a2: number, b1: number, b2: number) {
  if (b2 < a1) return a1 - b2;
  if (b1 > a2) return b1 - a2;
  return 0;
}

interface Cand {
  el: HTMLElement;
  primary: number; // distance along the movement axis
  ortho: number; // gap on the perpendicular axis (0 = aligned)
  center: number; // perpendicular centre offset
}

function candidates(from: DOMRect, dir: Dir, list: HTMLElement[], exclude: HTMLElement): Cand[] {
  const out: Cand[] = [];
  const horizontal = dir === 'left' || dir === 'right';
  for (const el of list) {
    if (el === exclude || el.contains(exclude)) continue;
    const r = el.getBoundingClientRect();
    let primary: number;
    let ortho: number;
    if (dir === 'right') ((primary = r.left - from.right), (ortho = rangeGap(from.top, from.bottom, r.top, r.bottom)));
    else if (dir === 'left') ((primary = from.left - r.right), (ortho = rangeGap(from.top, from.bottom, r.top, r.bottom)));
    else if (dir === 'down') ((primary = r.top - from.bottom), (ortho = rangeGap(from.left, from.right, r.left, r.right)));
    else ((primary = from.top - r.bottom), (ortho = rangeGap(from.left, from.right, r.left, r.right)));
    // Must actually lie in that direction (allow small overlaps from scaling effects).
    const along = horizontal ? (r.left + r.right) / 2 - (from.left + from.right) / 2 : (r.top + r.bottom) / 2 - (from.top + from.bottom) / 2;
    if (primary < -Math.min(24, (horizontal ? from.width : from.height) / 2)) continue;
    if ((dir === 'right' || dir === 'down') && along <= 0) continue;
    if ((dir === 'left' || dir === 'up') && along >= 0) continue;
    const center = horizontal ? (r.top + r.bottom) / 2 - (from.top + from.bottom) / 2 : (r.left + r.right) / 2 - (from.left + from.right) / 2;
    out.push({ el, primary: Math.max(primary, 0), ortho, center: Math.abs(center) });
  }
  return out;
}

/**
 * Pick the next element like a console does:
 * 1. "Beam" first: among elements aligned with the current one on the
 *    perpendicular axis, the nearest one wins — even if it is far away
 *    (so Down from the Play button reaches a section further down the same
 *    column instead of jumping to a closer side panel).
 * 2. Otherwise the closest element in that direction, weighting misalignment.
 *    Horizontal fallbacks are disabled inside grids, so Right at the end of a
 *    grid row does nothing instead of jumping to the next row.
 */
function best(from: DOMRect, dir: Dir, list: HTMLElement[], exclude: HTMLElement, opts: { allowOffAxis?: boolean } = {}) {
  const cands = candidates(from, dir, list, exclude);
  if (!cands.length) return null;
  const beam = cands.filter((c) => c.ortho === 0);
  if (beam.length) {
    beam.sort((a, b) => a.primary - b.primary || a.center - b.center);
    return beam[0]!.el;
  }
  if (opts.allowOffAxis === false) return null;
  let bestEl: HTMLElement | null = null;
  let bestScore = Infinity;
  for (const c of cands) {
    const score = c.primary + c.ortho * 2.5 + c.center * 0.05;
    if (score < bestScore) ((bestScore = score), (bestEl = c.el));
  }
  return bestEl;
}

/** Elements of the console bar (header) — a separate zone above the content. */
const inBar = (el: Element) => !!el.closest('[data-nav-zone="bar"]');
let lastContentFocus: HTMLElement | null = null;

export function moveFocus(dir: Dir) {
  const scope = topScope();
  const all = focusables(scope);
  if (!all.length) return;
  const cur = document.activeElement as HTMLElement | null;
  if (!cur || cur === document.body || !all.includes(cur)) return focusFirst();

  // Text fields keep left/right for the caret.
  if (isTextField(cur) && (dir === 'left' || dir === 'right')) {
    const len = cur.value.length;
    const pos = cur.selectionStart ?? 0;
    if ((dir === 'left' && pos > 0) || (dir === 'right' && pos < len)) return;
  }

  const from = cur.getBoundingClientRect();
  const horizontal = dir === 'left' || dir === 'right';
  const bar = all.filter(inBar);
  const content = all.filter((e) => !inBar(e));

  // --- In the console bar: Left/Right move along it, Down returns to the content.
  if (inBar(cur)) {
    if (horizontal) {
      const next = best(from, dir, bar, cur);
      if (next) focusEl(next);
      return;
    }
    if (dir === 'down') {
      const back = lastContentFocus && lastContentFocus.isConnected && content.includes(lastContentFocus) ? lastContentFocus : null;
      if (back) return focusEl(back, { center: true });
      const first = content.find((e) => e.getBoundingClientRect().bottom > from.bottom) ?? content[0];
      if (first) focusEl(first, { center: true });
    }
    return;
  }

  // --- In the content: the bar is only reached by going Up past the top.
  const row = cur.closest('[data-nav-row]');
  // Horizontal moves stay inside the current row when possible.
  if (row && horizontal) {
    const inRow = content.filter((e) => row.contains(e));
    const next = best(from, dir, inRow, cur);
    if (next) return focusEl(next);
    if (row.hasAttribute('data-nav-wrap')) return;
  }
  const inGrid = !!cur.closest('[data-nav-grid]');
  let next = best(from, dir, content, cur, { allowOffAxis: !(horizontal && inGrid) });
  if (!next) {
    if (dir === 'up' && bar.length) {
      lastContentFocus = cur;
      // Land on the active section tab when there is one.
      const active = bar.find((e) => e.classList.contains('active') && e.closest('.cbar-tabs')) ?? best(from, 'up', bar, cur) ?? bar[0]!;
      focusEl(active);
    }
    return;
  }
  // Entering another row: restore where the user was in it.
  const targetRow = next.closest('[data-nav-row]');
  if (targetRow && targetRow !== row && !horizontal) {
    const remembered = rowMemory.get(targetRow);
    if (remembered && remembered.isConnected && content.includes(remembered)) next = remembered;
  }
  focusEl(next, { center: !horizontal });
}

// ---------------------------------------------------------------------------
// Gamepad
// ---------------------------------------------------------------------------
const BUTTONS: Record<number, NavAction> = {
  0: 'accept',
  1: 'back',
  2: 'secondary',
  3: 'tertiary',
  4: 'prevTab',
  5: 'nextTab',
  6: 'prevSub',
  7: 'nextSub',
  9: 'menu',
  12: 'up',
  13: 'down',
  14: 'left',
  15: 'right',
};
const REPEATABLE = new Set<NavAction>(['up', 'down', 'left', 'right']);
const DEADZONE = 0.55;
const REPEAT_DELAY = 360;

// Polling uses a timer rather than requestAnimationFrame: some TV browsers and
// headless/background contexts throttle animation frames when nothing animates.
let timer: ReturnType<typeof setInterval> | 0 = 0;
const held = new Map<string, { since: number; last: number }>();
const connected = new Set<number>();
const padListeners = new Set<(pads: Gamepad[]) => void>();

export function onGamepadsChange(fn: (pads: Gamepad[]) => void) {
  padListeners.add(fn);
  fn(currentPads());
  return () => padListeners.delete(fn);
}
function currentPads() {
  return (navigator.getGamepads?.() ?? []).filter((g): g is Gamepad => !!g && g.connected);
}
function emitPads() {
  const pads = currentPads();
  for (const fn of padListeners) fn(pads);
}

function pollPads() {
  const now = performance.now();
  if (!connected.size) return stopPolling();
  if (document.hidden) return;
  const pads = currentPads();
  let anyPressed = false;
  const active = new Set<string>();
  for (const gp of pads) {
    const pressed = (i: number) => !!gp.buttons[i]?.pressed || (gp.buttons[i]?.value ?? 0) > 0.6;
    const ax = gp.axes[0] ?? 0;
    const ay = gp.axes[1] ?? 0;
    const state: [string, NavAction, boolean][] = [];
    for (const [i, action] of Object.entries(BUTTONS)) {
      let on = pressed(Number(i));
      if (action === 'up') on ||= ay < -DEADZONE;
      if (action === 'down') on ||= ay > DEADZONE;
      if (action === 'left') on ||= ax < -DEADZONE;
      if (action === 'right') on ||= ax > DEADZONE;
      state.push([`${gp.index}:${action}`, action, on]);
    }
    for (const [key, action, on] of state) {
      if (!on) continue;
      anyPressed = true;
      active.add(key);
      if (suspended || rearm) continue;
      const h = held.get(key);
      if (!h) {
        held.set(key, { since: now, last: now });
        setInput('gamepad');
        dispatchNav(action, 'gamepad');
      } else if (REPEATABLE.has(action) && now - h.since > REPEAT_DELAY) {
        // Accelerate the longer the direction is held.
        const rate = now - h.since > 1400 ? 55 : 110;
        if (now - h.last >= rate) {
          h.last = now;
          dispatchNav(action, 'gamepad', true);
        }
      }
    }
  }
  for (const key of held.keys()) if (!active.has(key)) held.delete(key);
  if (rearm && !anyPressed) rearm = false;
  if (suspended) held.clear();
}

function startPolling() {
  if (!timer && connected.size) timer = setInterval(pollPads, 16);
}
function stopPolling() {
  if (timer) clearInterval(timer);
  timer = 0;
}

// ---------------------------------------------------------------------------
// Install
// ---------------------------------------------------------------------------
let installed = false;

export function installNav() {
  if (installed) return () => {};
  installed = true;
  document.documentElement.dataset.input = input;

  const onKey = (e: KeyboardEvent) => {
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    if (!['Shift', 'Control', 'Alt', 'Meta'].includes(e.key)) setInput('keyboard');
    if (suspended) return;
    const el = document.activeElement;
    const text = isTextField(el);
    const map: Record<string, NavAction> = {
      ArrowUp: 'up',
      ArrowDown: 'down',
      ArrowLeft: 'left',
      ArrowRight: 'right',
      Escape: 'back',
      GoBack: 'back',
      BrowserBack: 'back',
      ContextMenu: 'menu',
      PageUp: 'prevTab',
      PageDown: 'nextTab',
    };
    let action = map[e.key];
    if (!action) return;
    // Selects and text areas keep their native arrow behaviour.
    if (el instanceof HTMLSelectElement && (action === 'up' || action === 'down')) return;
    if (el instanceof HTMLTextAreaElement && action !== 'back') return;
    if (text && action === 'back') {
      (el as HTMLElement).blur();
      e.preventDefault();
      return;
    }
    if (text && (action === 'left' || action === 'right')) {
      const pos = (el as HTMLInputElement).selectionStart ?? 0;
      const len = (el as HTMLInputElement).value.length;
      if ((action === 'left' && pos > 0) || (action === 'right' && pos < len)) return;
    }
    e.preventDefault();
    dispatchNav(action, 'keyboard', e.repeat);
  };

  let lastX = -1;
  let lastY = -1;
  const onPointer = (e: PointerEvent) => {
    if (e.pointerType === 'touch') return setInput('touch');
    // Ignore tiny jitter so a resting mouse doesn't steal focus mode from a gamepad.
    if (lastX >= 0 && Math.abs(e.clientX - lastX) + Math.abs(e.clientY - lastY) > 6) setInput('mouse');
    lastX = e.clientX;
    lastY = e.clientY;
  };
  const onTouch = () => setInput('touch');

  const onConnect = (e: GamepadEvent) => {
    connected.add(e.gamepad.index);
    emitPads();
    startPolling();
  };
  const onDisconnect = (e: GamepadEvent) => {
    connected.delete(e.gamepad.index);
    for (const k of held.keys()) if (k.startsWith(`${e.gamepad.index}:`)) held.delete(k);
    emitPads();
    if (!connected.size && input === 'gamepad') setInput('keyboard');
  };

  // Remember the last focused item of each row.
  const onFocusIn = (e: FocusEvent) => {
    const el = e.target as HTMLElement;
    const row = el.closest?.('[data-nav-row]');
    if (row) rowMemory.set(row, el);
  };

  // If the focused element disappears (list re-render, dialog closed) while
  // navigating without a mouse, recover focus instead of leaving it on <body>.
  const observer = new MutationObserver(() => {
    if (input !== 'gamepad' && input !== 'keyboard') return;
    if (suspended) return;
    if (document.activeElement === document.body || !document.activeElement) queueMicrotask(() => document.activeElement === document.body && focusFirst());
  });
  observer.observe(document.body, { childList: true, subtree: true });

  window.addEventListener('keydown', onKey);
  window.addEventListener('pointermove', onPointer, { passive: true });
  window.addEventListener('pointerdown', onPointer, { passive: true });
  window.addEventListener('touchstart', onTouch, { passive: true });
  window.addEventListener('gamepadconnected', onConnect);
  window.addEventListener('gamepaddisconnected', onDisconnect);
  document.addEventListener('focusin', onFocusIn);
  // Pads already connected before load (Chrome exposes them after a press).
  for (const gp of currentPads()) connected.add(gp.index);
  startPolling();

  return () => {
    installed = false;
    stopPolling();
    observer.disconnect();
    window.removeEventListener('keydown', onKey);
    window.removeEventListener('pointermove', onPointer);
    window.removeEventListener('pointerdown', onPointer);
    window.removeEventListener('touchstart', onTouch);
    window.removeEventListener('gamepadconnected', onConnect);
    window.removeEventListener('gamepaddisconnected', onDisconnect);
    document.removeEventListener('focusin', onFocusIn);
  };
}
